/**
 * Per-device preference for how shared files open.
 *
 * Some browsers block the new window/tab the app opens when you view a file
 * (a "pop-up"). This preference lets people choose to open files in the same
 * tab instead, so the browser never blocks them. Stored in localStorage with a
 * custom event so the profile toggle and every file list stay in sync.
 */
import { useCallback, useEffect, useState } from 'react';

const KEY = 'db-files-new-tab';
const EVT = 'db-files-new-tab-changed';

function read(): boolean {
  if (typeof window === 'undefined') return true; // default: open in a new tab
  return window.localStorage.getItem(KEY) !== 'off';
}

/** React hook for the "open files in a new tab" preference. */
export function useFileOpenPref() {
  const [newTab, setNewTabState] = useState<boolean>(read);

  useEffect(() => {
    const sync = () => setNewTabState(read());
    window.addEventListener(EVT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const setNewTab = useCallback((v: boolean) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(KEY, v ? 'on' : 'off');
      window.dispatchEvent(new Event(EVT));
    }
    setNewTabState(v);
  }, []);

  return { newTab, setNewTab };
}

/** Non-hook reader, safe to call inside async click handlers. */
export function prefersNewTab(): boolean {
  return read();
}

/**
 * Open a placeholder tab synchronously inside a click handler when the user
 * prefers new tabs. Opening it before any await is what dodges pop-up blockers
 * on flows that first fetch a signed URL. Returns null in same-tab mode.
 */
export function preOpenTab(): Window | null {
  if (typeof window === 'undefined' || !prefersNewTab()) return null;
  return window.open('', '_blank', 'noopener,noreferrer');
}

/**
 * Navigate to a file URL respecting the preference. Pass the window returned by
 * preOpenTab() for async flows. Falls back to the same tab if a pop-up was
 * blocked so the file still opens.
 */
export function openFileUrl(url: string, preOpened?: Window | null) {
  if (typeof window === 'undefined') return;
  if (!prefersNewTab()) {
    if (preOpened) {
      try { preOpened.close(); } catch { /* ignore */ }
    }
    window.location.href = url;
    return;
  }
  if (preOpened) {
    preOpened.location.href = url;
    return;
  }
  const w = window.open(url, '_blank', 'noopener,noreferrer');
  if (!w) window.location.href = url; // blocked → fall back to same tab
}

/**
 * Quick check that pop-ups are currently allowed. Opens a tiny confirmation
 * window the user can close. Returns false if the browser blocked it.
 */
export function testPopup(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window.open('', '_blank', 'width=480,height=360');
  if (!w) return false;
  try {
    w.document.title = 'Pop-up test';
    w.document.body.style.cssText =
      'font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#0f172a;color:#fff;text-align:center;padding:24px';
    w.document.body.innerHTML =
      '<div><h2 style="margin:0 0 8px">Pop-ups are enabled \u2713</h2><p style="opacity:.7;margin:0">You can close this window.</p></div>';
    setTimeout(() => { try { w.close(); } catch { /* ignore */ } }, 2500);
  } catch { /* ignore cross-origin quirks */ }
  return true;
}
